import { get, post, patch, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse, PaginationMeta } from '../../types/api';
import { Invoice, InvoiceItem, Payment, CreatePaymentDto, ChargeEvent, CreateInvoiceDto, InvoiceItemDiscount, TopSheet, DueCollectionsResult } from '../../types/billing';

export const billingService = {
  getTopSheet: (dateFrom?: string, dateTo?: string): Promise<ApiResponse<TopSheet>> => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    const qs = params.toString();
    return get<TopSheet>(`/billing/top-sheet${qs ? `?${qs}` : ''}`);
  },

  /** Filterable due-collection list behind the Billing / Top Sheet card. */
  getDueCollections: (filters: {
    dateFrom?: string;
    dateTo?: string;
    bucket?: 'current' | 'old';
    setting?: 'opd' | 'ipd' | 'pharmacy';
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<DueCollectionsResult>> => {
    const params = new URLSearchParams();
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.bucket) params.set('bucket', filters.bucket);
    if (filters.setting) params.set('setting', filters.setting);
    if (filters.search) params.set('search', filters.search);
    params.set('page', String(filters.page || 1));
    params.set('limit', String(filters.limit || 20));
    return get<DueCollectionsResult>(`/billing/due-collections?${params.toString()}`);
  },

  getInvoices: (
    page = 1,
    limit = 20,
    filters: { search?: string; status?: string; patientId?: string; dateFrom?: string; dateTo?: string } = {},
  ): Promise<ApiResponse<Invoice[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.patientId) params.set('patientId', filters.patientId);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    return get<Invoice[]>(`/billing/invoices?${params.toString()}`) as Promise<ApiResponse<Invoice[]> & { meta: PaginationMeta }>;
  },

  /** Invoice totals (billed / paid / due) for one patient. */
  getPatientInvoiceSummary: (
    patientId: string,
  ): Promise<ApiResponse<{ count: number; billed: number; paid: number; due: number }>> => {
    return get<{ count: number; billed: number; paid: number; due: number }>(
      `/billing/patients/${patientId}/summary`,
    );
  },

  getTrashedInvoices: (page = 1, limit = 20, search?: string): Promise<ApiResponse<Invoice[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<Invoice[]>(`/billing/invoices/trash?${params.toString()}`) as Promise<ApiResponse<Invoice[]> & { meta: PaginationMeta }>;
  },

  getTrashCount: (): Promise<ApiResponse<{ count: number }>> => {
    return get<{ count: number }>('/billing/invoices/trash/count');
  },

  restoreInvoices: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> => {
    return post<{ restored: string[]; notFound: string[] }>('/billing/invoices/trash/bulk-restore', { ids });
  },

  permanentlyDeleteInvoices: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/invoices/trash/bulk-delete', { ids });
  },

  /**
   * Fetch the invoice PDF as an object URL via an authenticated request. Used by
   * the print-to-printer flow, which needs the bytes rather than a download.
   */
  fetchInvoicePdfUrl: async (id: string): Promise<string> => {
    const response = await httpService.get(`/billing/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  getInvoice: (id: string): Promise<ApiResponse<Invoice>> => {
    return get<Invoice>(`/billing/invoices/${id}`);
  },

  getInvoiceItems: (id: string): Promise<ApiResponse<InvoiceItem[]>> => {
    return get<InvoiceItem[]>(`/billing/invoices/${id}/items`);
  },

  /**
   * Direct, authenticated URL for the generated invoice PDF.
   *
   * The endpoint responds with `Content-Disposition: attachment`, so a normal
   * navigation to this URL starts a native browser download. We deliberately do
   * NOT fetch it as an XHR/blob: the browser blocks the cross-origin XHR read
   * (CORS) but still saves the attachment, which surfaced as a spurious
   * "Network error" even though the PDF was downloaded.
   */
  getInvoicePdfUrl: (id: string): string =>
    `${httpService.defaults.baseURL ?? ''}/billing/invoices/${id}/pdf`,

  createInvoice: (data: CreateInvoiceDto): Promise<ApiResponse<Invoice>> => {
    return post<Invoice>('/billing/invoices', data);
  },

  updateInvoice: (
    id: string,
    data: Partial<CreateInvoiceDto> & { notes?: string; items?: InvoiceItemDiscount[] },
  ): Promise<ApiResponse<Invoice>> => {
    return patch<Invoice>(`/billing/invoices/${id}`, data);
  },

  /**
   * Change an invoice's status by hand. `cancelled` soft-cancels (reversible,
   * keeps the record); `issued` reopens a cancelled invoice.
   */
  updateInvoiceStatus: (id: string, status: string): Promise<ApiResponse<Invoice>> => {
    return patch<Invoice>(`/billing/invoices/${id}/status`, { status });
  },

  deleteInvoice: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/billing/invoices/${id}`);
  },

  bulkDeleteInvoices: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked: string[] }>(
      '/billing/invoices/bulk-delete',
      { ids },
    );
  },

  getUnbilledCharges: (visitId: string): Promise<ApiResponse<ChargeEvent[]>> => {
    return get<ChargeEvent[]>(`/billing/visits/${visitId}/unbilled-charges`);
  },

  getPayments: (filters?: { search?: string; invoiceId?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number }): Promise<ApiResponse<Payment[]> & { meta: PaginationMeta; stats?: { totalAmount?: number } }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.invoiceId) params.append('invoiceId', filters.invoiceId);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    params.append('page', String(filters?.page || 1));
    params.append('limit', String(filters?.limit || 20));
    return get<Payment[]>(`/billing/payments?${params.toString()}`) as Promise<ApiResponse<Payment[]> & { meta: PaginationMeta; stats?: { totalAmount?: number } }>;
  },

  getPayment: (id: string): Promise<ApiResponse<Payment>> => {
    return get<Payment>(`/billing/payments/${id}`);
  },

  getTrashedPayments: (page = 1, limit = 20, search?: string): Promise<ApiResponse<Payment[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<Payment[]>(`/billing/payments/trash?${params.toString()}`) as Promise<ApiResponse<Payment[]> & { meta: PaginationMeta }>;
  },

  getPaymentTrashCount: (): Promise<ApiResponse<{ count: number }>> => {
    return get<{ count: number }>('/billing/payments/trash/count');
  },

  restorePayments: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> => {
    return post<{ restored: string[]; notFound: string[] }>('/billing/payments/trash/bulk-restore', { ids });
  },

  permanentlyDeletePayments: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/payments/trash/bulk-delete', { ids });
  },

  bulkDeletePayments: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/payments/bulk-delete', { ids });
  },

  recordPayment: (data: CreatePaymentDto): Promise<ApiResponse<Payment>> => {
    return post<Payment>('/billing/payments', data);
  },

  processRefund: (data: { paymentId: string; amount: number; reason?: string; approvedBy?: string }): Promise<ApiResponse<{ refund: Payment; originalPayment: Payment }>> => {
    return post<{ refund: Payment; originalPayment: Payment }>('/billing/refunds', data);
  },

  getCashbook: (filters?: { dateFrom?: string; dateTo?: string; page?: number; limit?: number }): Promise<ApiResponse<any[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    params.append('page', String(filters?.page || 1));
    params.append('limit', String(filters?.limit || 20));
    return get(`/billing/accounts/cashbook?${params.toString()}`) as Promise<ApiResponse<any[]> & { meta: PaginationMeta }>;
  },

  getCashbookSummary: (filters?: { dateFrom?: string; dateTo?: string }): Promise<ApiResponse<{ opening: number; cashIn: number; cashOut: number; balance: number; riskFundOpd: number; riskFundIpd: number }>> => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return get(`/billing/accounts/cashbook/summary${qs ? `?${qs}` : ''}`);
  },

  openCashbook: (openingBalance: number, enteredById: string, shiftCode?: string): Promise<ApiResponse<any>> => {
    return post('/billing/accounts/cashbook/open', { openingBalance, enteredById, shiftCode });
  },

  closeCashbook: (
    closingBalance: number,
    enteredById: string,
    shiftCode?: string,
    varianceReason?: string,
  ): Promise<ApiResponse<any>> => {
    return post('/billing/accounts/cashbook/close', {
      closingBalance,
      enteredById,
      shiftCode,
      varianceReason,
    });
  },

  bulkDeleteCashbook: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/accounts/cashbook/bulk-delete', { ids });
  },

  getTrashedCashbook: (page = 1, limit = 20, search?: string): Promise<ApiResponse<any[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get(`/billing/accounts/cashbook/trash?${params.toString()}`) as Promise<ApiResponse<any[]> & { meta: PaginationMeta }>;
  },

  getCashbookTrashCount: (): Promise<ApiResponse<{ count: number }>> => {
    return get<{ count: number }>('/billing/accounts/cashbook/trash/count');
  },

  restoreCashbook: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> => {
    return post<{ restored: string[]; notFound: string[] }>('/billing/accounts/cashbook/trash/bulk-restore', { ids });
  },

  permanentlyDeleteCashbook: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/billing/accounts/cashbook/trash/bulk-delete', { ids });
  },
};
